<template>
  <div class="security-settings-page">
    <div class="page-header">
      <div>
        <p class="eyebrow">SECURITY</p>
        <h1>{{ screenName }}</h1>
        <!--
          **パスキーはパスワードの入口を閉じない**（束22）。

          以前は「2要素認証かパスキーを足すと、パスワードが漏れても入られなく
          なります」と書いていた。パスキーは入口を1つ増やすだけで、パスワードの
          入口を閉じられるのは会社単位の設定だけ。パスキーだけを足した人は、
          漏れたパスワードでそのまま入られる。2要素（認証アプリ）はどの入口にも
          効くので、ここは2要素についてだけ言う。
        -->
        <p>
          ログインの方法を増やしたり、入っている端末を切ったりできます。
          <b>2要素認証を足すと、パスワードが漏れても確認コードが無ければ入られなくなります</b>。
        </p>
        <!--
          **パスワードの変更は［個人設定］にある**（決めたこと 18）。置き場は
          移さず、ここに1行だけ案内する。ログインの方法がまとまった画面なので、
          ここで探す人がいる。
        -->
        <p class="security-password-pointer">
          パスワードの変更は<router-link :to="{ name: personalScreenName }">［個人設定］</router-link>から行えます。
        </p>
      </div>
    </div>

    <!--
      上端に出すのは読み込みの失敗だけ。端末を切った結果は「いま入っている端末」の
      カードの中に出す（そのカードは2要素認証・パスキー・外部アカウントの節の下にあり、
      上端は見えなかった）。
    -->
    <CAlert
      v-if="errorMessage"
      color="danger"
      close-button
      @update:show="errorMessage = ''"
    >{{ errorMessage }}</CAlert>

    <!--
      ログインの方法を先に、端末の一覧を後に置く。

      **利用者がここへ来る理由の多くは「2要素を付けたい」。**端末の一覧を
      先に出すと、目的のものが画面の下にあって見つけられない。
    -->
    <MfaSettings/>

    <PasskeySettings/>

    <ExternalAccountSettings/>

    <div v-if="loading" class="security-settings-loading">
      <CSpinner color="primary"/>
      <span>入っている端末を読み込んでいます。</span>
    </div>

    <CCard v-else class="settings-card">
      <CCardHeader>
        <div class="section-heading">
          <span class="section-icon"><CIcon name="cil-devices"/></span>
          <div>
            <strong>いま入っている端末</strong>
            <p>
              最後に使った順です。<b>この端末</b>と付いているものが、
              いま操作している{{ productName }}です。
            </p>
          </div>
        </div>
      </CCardHeader>

      <CCardBody>
        <ActionResultMessage :text="sessionMessage.text" :type="sessionMessage.type" />
        <p v-if="!sessions.length" class="security-empty">
          ログイン中の端末がありません。
        </p>

        <ul v-else class="security-session-list">
          <li
            v-for="item in sessions"
            :key="item.id"
            class="security-session"
            :class="{ 'security-session--current': item.current }"
          >
            <div class="security-session__body">
              <p class="security-session__title">
                {{ describeDevice(item) }}
                <span v-if="item.current" class="security-session__badge">
                  この端末
                </span>
              </p>
              <p class="security-session__detail">
                最後に使ったの: {{ formatTime(item.lastSeenAt) }}
              </p>
              <p class="security-session__detail">
                入った日時: {{ formatTime(item.createdAt) }}
                 / 認証方法: {{ describeMethod(item.authMethod) }}
                 / 接続元IP: {{ item.ipAddress || '不明' }}
              </p>
            </div>

            <!--
              いま使っている端末には、切るボタンを出さない。

              押せてしまうと、自分をその場で締め出す。「全部から出る」は
              ログアウトでできるので、ここでは出さない。
            -->
            <CButton
              v-if="!item.current"
              color="outline-danger"
              size="sm"
              :disabled="busy"
              @click="revoke(item)"
            >この端末を切る</CButton>
          </li>
        </ul>
      </CCardBody>

      <CCardFooter v-if="otherCount > 0">
        <!--
          いまの端末は残す。押した本人がその場で締め出されると、
          「心当たりのない端末がある」ときに何もできなくなる。
        -->
        <CButton
          color="outline-danger"
          :disabled="busy"
          @click="revokeOthers"
        >ほかの{{ otherCount }}台を全部切る</CButton>
        <p class="security-settings-note">
          いま操作しているこの端末は残ります。
        </p>
      </CCardFooter>
    </CCard>
  </div>
</template>

<script>
import {
  listSessions,
  revokeSession,
  revokeOtherSessions,
  isReauthenticationRequired,
  isNetworkFailure
} from '../../services/authSessions'
import {
  PERSONAL_SCREEN_NAME,
  SECURITY_SCREEN_NAME
} from '../../common/screenNames'
import { PRODUCT_NAME } from '../../common/productName'
import MfaSettings from './MfaSettings'
import PasskeySettings from './PasskeySettings'
import ExternalAccountSettings from './ExternalAccountSettings'
import ActionResultMessage from '../../components/ActionResultMessage.vue'

/**
 * 入り方の見せ方。
 *
 * **知らない値はそのまま出さない。** サーバが増やした値をそのまま画面へ
 * 出すと、表示が英大文字のまま混ざる。増えたら気づけるよう、既定は
 * 「その他」にしておく。
 */
const METHOD_LABELS = {
  PASSWORD: 'メールアドレスとパスワード',
  PASSKEY: 'パスキー',
  MICROSOFT: 'Microsoft アカウント',
  GOOGLE: 'Google アカウント',
  ENTERPRISE_SSO: '会社のログイン'
}

export default {
  name: 'SecuritySettings',

  components: {
    MfaSettings,
    PasskeySettings,
    ExternalAccountSettings,
    ActionResultMessage
  },

  data () {
    return {
      sessions: [],
      loading: true,
      busy: false,
      // 端末を切った結果。「いま入っている端末」のカードの中に出す。
      sessionMessage: { type: 'success', text: '' },
      // 読み込みの失敗だけ（画面の上端に出す）。
      errorMessage: ''
    }
  },

  computed: {
    /** 売るときの正式な表記。**1か所から取る。** */
    productName () {
      return PRODUCT_NAME
    },

    /** 見出し。**右上のメニューに出る名前と同じもの。** */
    screenName () {
      return SECURITY_SCREEN_NAME
    },

    /** パスワードの変更がある画面（右上のメニューと同じ名前）。 */
    personalScreenName () {
      return PERSONAL_SCREEN_NAME
    },

    /** いまの端末を除いた台数。 */
    otherCount () {
      return this.sessions.filter(item => !item.current).length
    }
  },

  mounted () {
    this.reload()
  },

  methods: {
    /**
     * @param quiet 切ったあとの読み直しでは true。**カードを作り直さない。**
     *   loading を立てるとカードが消えて作り直され、中に出した結果の文も消える。
     */
    async reload (quiet) {
      if (!quiet) this.loading = true
      try {
        this.sessions = await listSessions()
      } catch (error) {
        if (!quiet) this.sessions = []
        this.errorMessage = this.explain(error,
          '入っている端末を読み込めませんでした。')
      } finally {
        this.loading = false
      }
    },

    async revoke (item) {
      if (this.busy) return

      this.busy = true
      this.sessionMessage = { type: 'success', text: '' }
      try {
        const revoked = await revokeSession(item.id)
        /*
         * サーバは「切れたかどうか」しか返さない。切れなかったときに
         * 「切れました」と出すと、利用者は盗まれた端末を切ったつもりで
         * 放置する。**切れていないことは、そのまま伝える。**
         */
        this.sessionMessage = revoked
          ? { type: 'success', text: 'この端末を切りました。' }
          : {
            type: 'danger',
            text: 'この端末は切れませんでした。'
              + ' 画面を再読み込みしてから、もう一度お試しください。'
          }
        await this.reload(true)
      } catch (error) {
        this.sessionMessage = { type: 'danger', text: this.explain(error, '端末を切れませんでした。') }
      } finally {
        this.busy = false
      }
    },

    async revokeOthers () {
      if (this.busy) return

      this.busy = true
      this.sessionMessage = { type: 'success', text: '' }
      try {
        const revoked = await revokeOtherSessions()
        this.sessionMessage = {
          type: 'success',
          text: revoked > 0
            ? 'ほかの' + revoked + '台を切りました。'
            : 'ログアウトする端末が見つかりませんでした。'
        }
        await this.reload(true)
      } catch (error) {
        this.sessionMessage = { type: 'danger', text: this.explain(error, '端末を切れませんでした。') }
      } finally {
        this.busy = false
      }
    },

    /**
     * 断られた理由の見せ方。
     *
     * **本人確認のやり直しと、通信が届かないことだけを分ける。**
     * それ以外は1種類にまとめる。細かく分けると、応答の違いから
     * 外の人が中の状態を推せる。
     */
    explain (error, fallback) {
      if (isReauthenticationRequired(error)) {
        return 'もう一度ログインの確認が必要です。'
          + ' 一度ログアウトして再ログインしてから、もう一度お試しください。'
      }
      if (isNetworkFailure(error)) {
        return '通信できませんでした。接続を確認してからお試しください。'
      }
      return fallback
    },

    describeMethod (authMethod) {
      return METHOD_LABELS[authMethod] || 'その他'
    },

    /**
     * 端末の見せ方。
     *
     * **ユーザーエージェントをそのまま出さない。** 長くて読めないうえ、
     * 画面に出た文字列がそのまま他の場所へ貼られる。読める形に短くする。
     */
    describeDevice (item) {
      const agent = String(item.userAgent || '')
      if (!agent) return '不明な端末'

      const os = ['Windows', 'Mac', 'iPhone', 'iPad', 'Android', 'Linux']
        .find(name => agent.indexOf(name) >= 0)
      const browser = ['Edg', 'Chrome', 'Firefox', 'Safari']
        .find(name => agent.indexOf(name) >= 0)

      if (!os && !browser) return '不明な端末'
      return [os || '不明なOS', browser === 'Edg' ? 'Edge' : browser]
        .filter(Boolean).join(' / ')
    },

    formatTime (value) {
      if (!value) return '不明'
      const at = new Date(value)
      if (isNaN(at.getTime())) return '不明'
      return at.toLocaleString('ja-JP')
    }
  }
}
</script>
